import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Stethoscope, NotebookPen, BookOpen, Download,
  LifeBuoy, UserCog, LogOut, Menu, X, ShieldCheck, Lock,
  Sparkles, Activity, FileText, Leaf, FlaskConical, LineChart, FolderHeart,
} from "lucide-react";
import AsaLogo from "./AsaLogo";
import { useAsaAuth } from "../AuthContext";
import { hasFullAccess } from "../data";
import { toast } from "sonner";

const nav = [
  { to: "/asa/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/asa/symptom-checker", label: "Symptom Awareness Check", icon: Stethoscope },
  { to: "/asa/diary", label: "Symptom Diary", icon: NotebookPen, premium: true },
  { to: "/asa/progress", label: "My Progress", icon: LineChart, premium: true },
  { to: "/asa/records", label: "My Records", icon: FolderHeart, premium: true },
  { to: "/asa/insights", label: "AI Insights", icon: Sparkles, premium: true },
  { to: "/asa/flare-prediction", label: "Symptom Forecast", icon: Activity, premium: true },
  { to: "/asa/gp-report", label: "GP Report", icon: FileText, premium: true },
  { to: "/asa/trials", label: "Clinical Trials", icon: FlaskConical, premium: true },
  { to: "/asa/library", label: "Education Library", icon: BookOpen },
  { to: "/asa/food-guide", label: "Food & Additives", icon: Leaf },
  { to: "/asa/reports", label: "Reports & Tools", icon: Download, premium: true },
  { to: "/asa/support", label: "ASAL Support", icon: LifeBuoy },
  { to: "/asa/account", label: "Account", icon: UserCog },
];

export default function AppLayout({ children, title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAsaAuth();
  const [open, setOpen] = useState(false);
  const fullAccess = hasFullAccess(user);

  const doLogout = () => { logout(); navigate("/asa"); };

  return (
    <div className="flex h-screen bg-[#f4f8f7] overflow-hidden">
      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed lg:static z-40 top-0 left-0 h-full w-[260px] bg-white border-r border-slate-200 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
          <AsaLogo size={30} />
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto kl-scroll">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            const locked = item.premium && !fullAccess;
            return (
              <button key={item.to} data-testid={locked ? `nav-locked-${item.to.split("/").pop()}` : undefined} onClick={() => {
                  if (locked) toast.message(`${item.label} is an ASAL Pro feature — subscribe to unlock.`, { description: "Upgrade to see more.", action: { label: "See Pro", onClick: () => navigate("/asa/pricing") } });
                  navigate(item.to); setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14.5px] font-medium transition-colors ${active ? "bg-[#e6f4f2] text-[#0f766e]" : "text-slate-600 hover:bg-slate-50"}`}>
                <Icon size={19} className={active ? "text-[#0d9488]" : "text-slate-400"} />
                {item.label}
                {locked && (
                  <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-bold text-[#b45309] bg-[#fef3c7] px-1.5 py-0.5 rounded" title="Unlock with ASAL Pro"><Lock size={9} /> PRO</span>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-100">
          {!fullAccess && (
            <button data-testid="sidebar-upgrade-btn" onClick={() => { navigate("/asa/pricing"); setOpen(false); }}
              className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-[13.5px] font-semibold text-white bg-gradient-to-r from-[#0d9488] to-[#0f766e] hover:opacity-95 transition-opacity">
              <Sparkles size={16} /> Unlock ASAL Pro
            </button>
          )}
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[14.5px] font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors">
            <LogOut size={19} className="text-slate-400" /> Log Out
          </button>
          <p className="text-[10.5px] text-slate-400 mt-3 px-1 leading-relaxed">
            © {new Date().getFullYear()} ASA Foundation.<br />Copyright &amp; IP: Asal Shirazi.
          </p>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6 sticky top-0 z-20">
          <button className="lg:hidden text-slate-500" onClick={() => setOpen(true)}><Menu size={22} /></button>
          <h1 className="font-head font-semibold text-slate-800 text-lg">{title}</h1>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-2.5 py-1 rounded-full">
              <ShieldCheck size={13} /> {fullAccess ? "ASAL Pro" : "ASAL Essential (Free)"}
            </span>
            <div className="w-9 h-9 rounded-full bg-[#0d9488] text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto kl-scroll p-4 lg:p-6">
          <div className="max-w-6xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
